import { UntitledIcon } from '@faceup/icons'
import { ulMicrophone01 } from '@faceup/icons/ulMicrophone01'
import { AudioPlayer, UnstyledButton } from '@faceup/ui'
import { type TextAreaProps as ANTTextAreaProps, Input, Tooltip } from '@faceup/ui-base'
import { VOICE_MESSAGE_MAX_COUNT } from '@faceup/utils'

export type VoiceRecording = {
  id: string
  durationMs: number
  blobUrl?: string
  file?: File
}

export type TextAreaWithVoiceRecordingsProps = Omit<ANTTextAreaProps, 'value' | 'defaultValue'> & {
  value?: string
  voiceRecordings: VoiceRecording[]
  onRemoveRecording: (recordingId: string) => void
  onVoiceRecordingButtonClick: () => void
  voiceSubmissionEnabled?: boolean
  maxVoiceRecordings?: number
  recordingLabel: (index: number) => string
  voiceRecordingButtonLabel: string
  voiceRecordingButtonMaxReachedLabel: string
}

const formatDuration = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export const TextAreaWithVoiceRecordings = ({
  voiceRecordings,
  onRemoveRecording,
  onVoiceRecordingButtonClick,
  voiceSubmissionEnabled = true,
  maxVoiceRecordings = VOICE_MESSAGE_MAX_COUNT,
  recordingLabel,
  voiceRecordingButtonLabel,
  voiceRecordingButtonMaxReachedLabel,
  ...textAreaProps
}: TextAreaWithVoiceRecordingsProps) => {
  const isVoiceRecordingButtonDisabled = voiceRecordings.length >= maxVoiceRecordings

  return (
    <div className='flex flex-col gap-4'>
      <div className='relative'>
        <Input.TextArea {...textAreaProps} />
        {voiceSubmissionEnabled && (
          <Tooltip
            title={
              isVoiceRecordingButtonDisabled
                ? voiceRecordingButtonMaxReachedLabel
                : voiceRecordingButtonLabel
            }
          >
            <UnstyledButton
              aria-label={
                isVoiceRecordingButtonDisabled
                  ? voiceRecordingButtonMaxReachedLabel
                  : voiceRecordingButtonLabel
              }
              disabled={isVoiceRecordingButtonDisabled}
              onClick={onVoiceRecordingButtonClick}
              className='absolute left-3 bottom-3 leading-0 hover:text-colorPrimaryHover'
            >
              <UntitledIcon icon={ulMicrophone01} size={16} />
            </UnstyledButton>
          </Tooltip>
        )}
      </div>
      {voiceRecordings.length > 0 && (
        <ul aria-live='polite' className='flex flex-col gap-2 list-none p-0 m-0'>
          {voiceRecordings.map((recording, index) => (
            <li key={recording.id}>
              <AudioPlayer
                label={recordingLabel(index + 1)}
                duration={formatDuration(recording.durationMs)}
                onPlay={
                  recording.blobUrl ? () => Promise.resolve(recording.blobUrl as string) : undefined
                }
                onRemove={async () => onRemoveRecording(recording.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
